import { useEffect, useRef, type CSSProperties } from 'react';
import { gameColors } from '@/shared/ui/game-theme';

interface HPBarProps {
  current: number;
  max: number;
  /** Bar color, defaults to health red */
  color?: string;
  /** Height in pixels */
  height?: number;
  /** Show numeric text */
  showText?: boolean;
  /** Label prefix (e.g., "HP", "Enemy") */
  label?: string;
}

const containerStyle: CSSProperties = {
  width: '100%',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: '#142023',
  border: '1px solid #937750',
};

const textStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: gameColors.text,
  fontSize: 11,
  fontWeight: 500,
  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
  pointerEvents: 'none',
};

export function HPBar({
  current,
  max,
  color = gameColors.health,
  height = 20,
  showText = true,
  label,
}: HPBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;

  // Animate width via ref to avoid React re-renders on every tick
  useEffect(() => {
    if (barRef.current) {
      barRef.current.style.transform = `scaleX(${ratio})`;
    }
  }, [ratio]);

  // Health keeps its red identity at every remaining amount.
  const barColor = color;

  const barStyle: CSSProperties = {
    height: '100%',
    width: '100%',
    transform: `scaleX(${ratio})`,
    transformOrigin: 'left center',
    backgroundColor: barColor,
    backgroundImage: 'linear-gradient(#ffffff16, #111a1d33)',
    boxShadow: 'inset 0 1px #e5c69433',
    transition: 'transform 0.2s ease-out, background-color 0.3s ease',
    borderRadius: 4,
  };

  return (
    <div className="th-hp-track" role="progressbar" aria-label={label || 'HP'} aria-valuenow={current} aria-valuemin={0} aria-valuemax={max} style={{ ...containerStyle, height }}>
      <div ref={barRef} style={barStyle} />
      {showText && (
        <div style={textStyle}>
          {label ? `${label}: ` : ''}{Math.ceil(current)} / {Math.ceil(max)}
        </div>
      )}
    </div>
  );
}
